import React, { useEffect, useState } from "react";
import { Bug, Loader, Save } from "lucide-react";
import { useApi } from "../hooks/useApi";

const STAGE_LABEL = {
  Legacy: "Old CVE data",
  LegacyStopped: "Old CVE data (collection stopped)",
  Ingesting: "Collecting the new data",
  ReadersOn: "New CVE data",
  RisksOn: "New CVE data and CVE risks",
};

const when = (value) => (value ? new Date(value.endsWith("Z") ? value : `${value}Z`).toLocaleString() : null);

// Platform Config → CVE: the organization's switch from its old CVE data to the new CVE pipeline (Defender's daily
// vulnerability snapshot), and optionally to CVE risks. Every organization, old or new, is switched on here; each change is
// logged with the name of the person who made it.
export default function CveSettingsPanel() {
  const apiCall = useApi();
  const [setting, setSetting] = useState(null);
  const [useNew, setUseNew] = useState(false);
  const [risks, setRisks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const show = (s) => {
    setSetting(s);
    setUseNew(!!s.usesNewPipeline);
    setRisks(!!s.createsRisks);
  };

  useEffect(() => {
    apiCall("GET", "/cve-settings").then(show).catch(() => setError("The CVE setting couldn't be loaded."));
  }, [apiCall]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      show(await apiCall("PUT", "/cve-settings", { useNewPipeline: useNew, createRisks: useNew && risks }));
      setSaved("Saved.");
    } catch (err) {
      setError(err?.response?.data?.message || "The CVE setting couldn't be saved.");
    } finally {
      setSaving(false);
    }
  };

  if (!setting && !error) {
    return <div className="flex justify-center py-10"><Loader className="h-6 w-6 animate-spin text-purple-600" /></div>;
  }

  const changed = setting && (useNew !== !!setting.usesNewPipeline || (useNew && risks) !== !!setting.createsRisks);
  return (
    <div className="bg-theme-secondary rounded-xl border border-border p-6 mb-5" data-testid="cve-settings">
      <div className="flex items-center gap-2 mb-1">
        <Bug className="h-5 w-5 text-purple-700" />
        <h2 className="text-base font-semibold text-content-primary">CVE vulnerability data</h2>
        {setting?.usesNewPipeline && (
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
        )}
      </div>
      <p className="text-sm text-content-secondary mb-4">
        Reads Microsoft Defender's daily vulnerability snapshot for this organization and stores only what changed. Switch it on
        here for each organization, old or new. Until the first snapshot is processed, the SOC shows "CVE collection starting".
      </p>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      {setting && !setting.featureOn && (
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800" data-testid="cve-settings-feature-off">
          The CVE pipeline is switched off for this installation, so nothing is collected yet. The setting is saved and takes effect when it is switched on.
        </div>
      )}

      {setting && (
        <>
          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input id="cve-use-new" type="checkbox" className="mt-0.5 rounded border-gray-300 text-purple-600"
                checked={useNew} onChange={(e) => { setUseNew(e.target.checked); if (!e.target.checked) setRisks(false); }} />
              <span className="text-sm text-content-primary">Use the new CVE data for this organization</span>
            </label>
            <label className={`flex items-start gap-2 ml-6 ${useNew ? "cursor-pointer" : "opacity-50"}`}>
              <input id="cve-create-risks" type="checkbox" className="mt-0.5 rounded border-gray-300 text-purple-600"
                checked={useNew && risks} disabled={!useNew} onChange={(e) => setRisks(e.target.checked)} />
              <span className="text-sm text-content-primary">
                Create CVE risks (one risk per serious CVE, with AI text and financial figures; old device-level risks with work on them are merged in)
              </span>
            </label>
          </div>

          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div><dt className="text-content-muted">Now</dt><dd className="text-content-primary" data-testid="cve-settings-stage">{STAGE_LABEL[setting.stage] || setting.stage}</dd></div>
            <div><dt className="text-content-muted">Latest snapshot</dt><dd className="text-content-primary">{when(setting.publishedSnapshotAt) || "None yet"}</dd></div>
            <div><dt className="text-content-muted">Last changed</dt>
              <dd className="text-content-primary" data-testid="cve-settings-changed-by">
                {setting.lastChangedBy ? `${setting.lastChangedBy}, ${when(setting.lastChangedAt)}` : "Never"}
              </dd></div>
          </dl>

          <div className="mt-4 flex items-center gap-3">
            <button onClick={save} disabled={saving || !changed}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-purple-700 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? <><Loader className="h-4 w-4 animate-spin" />Saving…</> : <><Save className="h-4 w-4" />Save</>}
            </button>
            {saved && <span className="text-sm text-green-700">{saved}</span>}
          </div>
        </>
      )}
    </div>
  );
}
